// Typed, namespaced localStorage wrapper. Every walkit save path is a try/catch around
// JSON.parse/stringify (loadSave/saveSave); this is that pattern, typed and reusable, so a
// game gets a small persisted store without hand-rolling the guards each time.

export interface SaveStore<T extends object> {
  /** Current in-memory value (also what's persisted). */
  readonly value: Readonly<T>;
  /** Read one key. */
  get<K extends keyof T>(key: K): T[K];
  /** Write one key and persist. */
  set<K extends keyof T>(key: K, val: T[K]): void;
  /** Merge a partial patch and persist. */
  patch(partial: Partial<T>): void;
  /** Reset to defaults and persist. */
  reset(): void;
}

/**
 * Create a persisted store under `key`, seeded with `defaults`.
 * All reads/writes are guarded — a corrupt or unavailable localStorage never throws.
 */
export function createSave<T extends object>(key: string, defaults: T): SaveStore<T> {
  const load = (): T => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { ...defaults };
      const parsed = JSON.parse(raw) as Partial<T>;
      return { ...defaults, ...parsed }; // defaults fill any missing keys across versions
    } catch {
      return { ...defaults };
    }
  };

  let state: T = load();

  const persist = () => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      /* quota / private mode — ignore */
    }
  };

  return {
    get value() {
      return state;
    },
    get(k) {
      return state[k];
    },
    set(k, v) {
      state[k] = v;
      persist();
    },
    patch(partial) {
      state = { ...state, ...partial };
      persist();
    },
    reset() {
      state = { ...defaults };
      persist();
    },
  };
}
